import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { GiftCard, GiftCardDetail, IssueGiftCardBody } from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const GIFT_CARDS_PATH = '/api/restaurants/{restaurantId}/gift-cards';

const listKey = (restaurantId: string) => ['gift-cards', restaurantId] as const;
const oneKey = (restaurantId: string, giftCardId: string) =>
  ['gift-card', restaurantId, giftCardId] as const;

export function useGiftCards() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: listKey(restaurantId),
    queryFn: async () =>
      unwrapResult<GiftCard[]>(
        await openapiClient.GET(GIFT_CARDS_PATH, { params: { path: { restaurantId } } }),
      ),
    enabled: !!restaurantId,
  });
}

export function useGiftCard(giftCardId: string | null) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: oneKey(restaurantId, giftCardId ?? ''),
    queryFn: async () =>
      unwrapResult<GiftCardDetail>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/gift-cards/{giftCardId}', {
          params: { path: { restaurantId, giftCardId: giftCardId ?? '' } },
        }),
      ),
    enabled: !!restaurantId && !!giftCardId,
  });
}

export function useIssueGiftCard() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: IssueGiftCardBody) =>
      unwrapResult<GiftCard>(
        await openapiClient.POST(GIFT_CARDS_PATH, { params: { path: { restaurantId } }, body }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listKey(restaurantId) }),
  });
}

export function useVoidGiftCard() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ giftCardId, reason }: { giftCardId: string; reason?: string }) =>
      unwrapResult<GiftCard>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/gift-cards/{giftCardId}/void', {
          params: { path: { restaurantId, giftCardId } },
          body: { reason },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gift-card', restaurantId] }),
  });
}

export function useAdjustGiftCard() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      giftCardId,
      amountCents,
      reason,
    }: {
      giftCardId: string;
      amountCents: number;
      reason: string;
    }) =>
      unwrapResult<GiftCard>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/gift-cards/{giftCardId}/adjust', {
          params: { path: { restaurantId, giftCardId } },
          body: { amountCents, reason },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gift-card', restaurantId] }),
  });
}
